import { Quat } from '@/core/math/Quat';
import { Vec3 } from '@/core/math/Vec3';
import { clamp, clamp01 } from '@/core/math/scalar';
import { FieldDriveMode } from './FieldAscentDrive';
import { EngineEnvironment } from './Propulsion';
import { FlightEnvironment, Spacecraft } from './Spacecraft';
import { DEFAULT_GOVERNOR_MARGIN, throttleGovernor } from './ThrottleGovernor';

export type AlignmentTarget =
  | 'none'
  | 'prograde'
  | 'retrograde'
  | 'radialIn'
  | 'radialOut'
  | 'normal'
  | 'antiNormal'
  | 'target'
  | 'antiTarget'
  | 'surfaceUp'
  | 'surfaceProgradeLevel'
  | 'maneuver'
  | 'custom';

export interface NavigationBasis {
  prograde: Vec3;
  retrograde: Vec3;
  radialOut: Vec3;
  radialIn: Vec3;
  normal: Vec3;
  antiNormal: Vec3;
  /** Unit vector toward the current target, if one is selected. */
  target: Vec3 | null;
  /** Relative velocity to the target, if one is selected. */
  targetRelativeVelocity: Vec3 | null;
}

export interface HoldState {
  /** Damp residual rotation to zero when no rotation input is present. */
  rotationDamping: boolean;
  /** Hold the current attitude. */
  attitudeHold: boolean;
  /** Hold a chosen alignment. */
  alignment: AlignmentTarget;
  /** Hold a velocity vector in the reference frame. */
  velocityHold: boolean;
  velocityTarget: Vec3;
  /** Hold altitude above terrain. */
  altitudeHold: boolean;
  altitudeTarget: number;
  /** Hold a vertical speed. */
  verticalSpeedHold: boolean;
  verticalSpeedTarget: number;
  /** Zero vertical velocity using whatever propulsion is available. */
  hoverAssist: boolean;
  /** Null relative velocity against the selected target. */
  matchTargetVelocity: boolean;
}

export function defaultHoldState(): HoldState {
  return {
    rotationDamping: true,
    attitudeHold: false,
    alignment: 'none',
    velocityHold: false,
    velocityTarget: new Vec3(),
    altitudeHold: false,
    altitudeTarget: 0,
    verticalSpeedHold: false,
    verticalSpeedTarget: 0,
    hoverAssist: false,
    matchTargetVelocity: false,
  };
}

export interface PilotInput {
  /** Body-axis rotation stick, -1..1. x = pitch, y = yaw, z = roll. */
  rotation: Vec3;
  /** Body-axis translation stick, -1..1. */
  translation: Vec3;
  /** Main throttle, 0..1. */
  throttle: number;
}

export interface AssistFailure {
  system: string;
  reason: string;
}

/**
 * Time-optimal (bang-bang) attitude slew.
 *
 * Rather than a naive PD loop, the desired angular rate is
 *
 *     omega_desired = sign(e) * sqrt(2 * alpha_max * |e|)
 *
 * which is the fastest rate from which the vessel can still stop exactly on
 * target using the available angular acceleration. The torque demand is then
 * whatever is needed to reach that rate. This gives crisp, non-overshooting
 * pointing with no gain tuning per vessel, and it degrades correctly when the
 * available torque drops (damaged thrusters, empty monopropellant).
 */
function attitudeTorqueDemand(
  errorRotationBody: Vec3,
  angularVelocityBody: Vec3,
  inertia: Vec3,
  maxAngularAccel: Vec3,
  rateLimit: number,
  out = new Vec3(),
): Vec3 {
  const axes: (keyof Pick<Vec3, 'x' | 'y' | 'z'>)[] = ['x', 'y', 'z'];
  for (const axis of axes) {
    const e = errorRotationBody[axis];
    const w = angularVelocityBody[axis];
    const aMax = Math.max(1e-6, maxAngularAccel[axis]);
    const desired = clamp(Math.sign(e) * Math.sqrt(2 * aMax * Math.abs(e)), -rateLimit, rateLimit);
    // Aim to reach `desired` within roughly two control periods.
    const accel = clamp((desired - w) * 6, -aMax, aMax);
    out[axis] = accel * inertia[axis];
  }
  return out;
}

/**
 * The flight computer.
 *
 * It reads the vessel and its environment, decides what forces and torques are
 * wanted, and issues actuator commands. It never writes position, velocity or
 * orientation, and it never exceeds what the engines, thrusters, power bus and
 * thermal limits actually allow: every hold mode goes through the same
 * saturation checks and reports a failure when it cannot deliver.
 *
 * There is one control law, not a switchable set of them: the stick commands
 * an attitude *rate*, releasing it damps the residual rotation to zero, and
 * fully releasing it holds whatever attitude the vessel ended up with. A raw
 * torque-passthrough mode existed alongside this and only ever made the vessel
 * harder to fly by hand.
 */
export class FlightComputer {
  readonly holds: HoldState = defaultHoldState();
  /** Custom attitude target, used when `alignment` is 'custom' or attitude hold. */
  readonly customAttitude = Quat.identity();
  /** Direction the manoeuvre planner wants the nose pointed, if any. */
  maneuverDirection: Vec3 | null = null;
  /** Selected target position and velocity in the vessel's frame. */
  targetPosition: Vec3 | null = null;
  targetVelocity: Vec3 | null = null;

  /** Maximum commanded rotation rate, rad/s. */
  rateLimit = 0.6;

  /** Safety margin the always-on throttle governor flies to, 0..1. */
  governorMargin = DEFAULT_GOVERNOR_MARGIN;

  readonly failures: AssistFailure[] = [];
  private heldAttitude: Quat | null = null;

  /** Torque the computer asked for last step, body frame. Diagnostics only. */
  readonly lastTorqueDemand = new Vec3();
  /** Force the computer asked for last step, body frame. Diagnostics only. */
  readonly lastForceDemand = new Vec3();

  /** Builds the navigation basis used by alignment modes and the HUD. */
  navigationBasis(ship: Spacecraft): NavigationBasis {
    const v = ship.body.velocity;
    const r = ship.body.position;
    const prograde = v.lengthSq() > 1e-12 ? v.normalized() : new Vec3(0, 0, 1);
    const radialOut = r.lengthSq() > 1e-12 ? r.normalized() : new Vec3(0, 1, 0);
    let normal = Vec3.crossVectors(r, v);
    if (normal.lengthSq() < 1e-12) normal = Vec3.anyPerpendicular(radialOut);
    normal.normalize();

    let target: Vec3 | null = null;
    let targetRelativeVelocity: Vec3 | null = null;
    if (this.targetPosition) {
      const delta = Vec3.subVectors(this.targetPosition, r);
      if (delta.lengthSq() > 1e-9) target = delta.normalize();
      if (this.targetVelocity) targetRelativeVelocity = Vec3.subVectors(this.targetVelocity, v);
    }

    return {
      prograde,
      retrograde: prograde.clone().negate(),
      radialOut,
      radialIn: radialOut.clone().negate(),
      normal,
      antiNormal: normal.clone().negate(),
      target,
      targetRelativeVelocity,
    };
  }

  /** Resolves the current alignment mode into a desired nose direction. */
  desiredNoseDirection(env: FlightEnvironment, nav: NavigationBasis): Vec3 | null {
    switch (this.holds.alignment) {
      case 'prograde':
        return nav.prograde;
      case 'retrograde':
        return nav.retrograde;
      case 'radialOut':
        return nav.radialOut;
      case 'radialIn':
        return nav.radialIn;
      case 'normal':
        return nav.normal;
      case 'antiNormal':
        return nav.antiNormal;
      case 'target':
        return nav.target;
      case 'antiTarget':
        return nav.target ? nav.target.clone().negate() : null;
      case 'surfaceUp':
        return env.up;
      case 'surfaceProgradeLevel': {
        // Prograde projected into the local horizontal plane: the attitude an
        // aircraft wants during atmospheric cruise.
        const horizontal = nav.prograde.rejectFromUnit(env.up);
        return horizontal.lengthSq() > 1e-9 ? horizontal.normalize() : null;
      }
      case 'maneuver':
        return this.maneuverDirection;
      case 'custom':
        return this.customAttitude.axisZ();
      default:
        return null;
    }
  }

  /**
   * Main entry point. Called once per physics step before `Spacecraft.step`.
   */
  update(ship: Spacecraft, env: FlightEnvironment, input: PilotInput, dt: number): void {
    this.failures.length = 0;

    const engineEnv: EngineEnvironment = { pressure: env.pressure, density: env.density };
    const nav = this.navigationBasis(ship);

    // ---- Attitude --------------------------------------------------------
    const torqueBody = new Vec3();
    const omegaBody = ship.body.orientation.rotateInverse(ship.body.angularVelocity);

    const maxWheel = ship.def.reactionWheelTorque;
    const rcsTorque = new Vec3(
      ship.allocator.maxTorqueAbout(new Vec3(1, 0, 0)),
      ship.allocator.maxTorqueAbout(new Vec3(0, 1, 0)),
      ship.allocator.maxTorqueAbout(new Vec3(0, 0, 1)),
    );
    const totalTorque = new Vec3(rcsTorque.x + maxWheel, rcsTorque.y + maxWheel, rcsTorque.z + maxWheel);
    const inertia = ship.massProperties.inertia;
    const maxAngularAccel = new Vec3(
      totalTorque.x / inertia.x,
      totalTorque.y / inertia.y,
      totalTorque.z / inertia.z,
    );

    const stick = input.rotation;
    const stickActive = stick.lengthSq() > 1e-6;

    const desiredNose = this.desiredNoseDirection(env, nav);
    const wantHold = !stickActive && (this.holds.attitudeHold || desiredNose !== null);

    if (wantHold) {
      // Attitude hold locks whatever orientation the vessel had when the stick
      // was last released, unless an alignment mode names a direction instead.
      const targetQuat = desiredNose
        ? this.buildAttitude(desiredNose, env, nav)
        : (this.heldAttitude ??= ship.body.orientation.clone());
      const errorWorld = ship.body.orientation.errorTo(targetQuat);
      const errorBody = ship.body.orientation.rotateInverse(errorWorld);
      attitudeTorqueDemand(errorBody, omegaBody, inertia, maxAngularAccel, this.rateLimit, torqueBody);
    } else {
      this.heldAttitude = null;
      if (stickActive) {
        // Rate command: the stick asks for an angular velocity, the computer
        // produces the torque needed to reach it.
        const desiredRate = new Vec3(
          stick.x * this.rateLimit,
          stick.y * this.rateLimit,
          stick.z * this.rateLimit,
        );
        for (const axis of ['x', 'y', 'z'] as const) {
          const accel = clamp((desiredRate[axis] - omegaBody[axis]) * 4, -maxAngularAccel[axis], maxAngularAccel[axis]);
          torqueBody[axis] = accel * inertia[axis];
        }
      } else if (this.holds.rotationDamping) {
        for (const axis of ['x', 'y', 'z'] as const) {
          const accel = clamp(-omegaBody[axis] * 4, -maxAngularAccel[axis], maxAngularAccel[axis]);
          torqueBody[axis] = accel * inertia[axis];
        }
      }
    }

    // ---- Translation -----------------------------------------------------
    const forceBody = new Vec3();
    const maxRcsForce = new Vec3(
      ship.allocator.maxForceAlong(new Vec3(1, 0, 0)),
      ship.allocator.maxForceAlong(new Vec3(0, 1, 0)),
      ship.allocator.maxForceAlong(new Vec3(0, 0, 1)),
    );
    forceBody.set(
      input.translation.x * maxRcsForce.x,
      input.translation.y * maxRcsForce.y,
      input.translation.z * maxRcsForce.z,
    );

    this.applyVelocityHolds(ship, env, nav, forceBody, maxRcsForce);

    // ---- Actuator dispatch ----------------------------------------------
    this.lastTorqueDemand.copy(torqueBody);
    this.lastForceDemand.copy(forceBody);

    // Reaction wheels first: they cost power but no propellant.
    const wheelDemand = new Vec3(
      clamp(torqueBody.x / Math.max(1e-6, maxWheel), -1, 1),
      clamp(torqueBody.y / Math.max(1e-6, maxWheel), -1, 1),
      clamp(torqueBody.z / Math.max(1e-6, maxWheel), -1, 1),
    );
    ship.reactionWheelDemand.copy(wheelDemand);

    const residualTorque = new Vec3(
      torqueBody.x - wheelDemand.x * maxWheel,
      torqueBody.y - wheelDemand.y * maxWheel,
      torqueBody.z - wheelDemand.z * maxWheel,
    );

    // Clear RCS commands so the allocator starts from zero each step.
    for (const e of ship.rcsThrusters) e.commandedThrottle = 0;

    if (ship.fuelOfType('monopropellant') <= 0 && (residualTorque.lengthSq() > 0 || forceBody.lengthSq() > 0)) {
      this.failures.push({ system: 'RCS', reason: 'Monopropellant depleted' });
    } else if (residualTorque.lengthSq() > 0 || forceBody.lengthSq() > 0) {
      const result = ship.allocator.solve({ force: forceBody, torque: residualTorque }, ship.def.size[2]);
      if (result.saturated) {
        this.failures.push({ system: 'Attitude control', reason: 'Control authority saturated' });
      }
    }

    // Control surfaces are only useful where there is air to push against.
    if (env.density > 0.001 && ship.def.aero.controlSurfaceArea > 0) {
      const q = ship.lastAero?.dynamicPressure ?? 0;
      const authority = clamp01(q / 2000);
      ship.controlSurfaces.set(
        clamp(torqueBody.x / Math.max(1, totalTorque.x), -1, 1) * authority,
        clamp(torqueBody.y / Math.max(1, totalTorque.y), -1, 1) * authority,
        clamp(torqueBody.z / Math.max(1, totalTorque.z), -1, 1) * authority,
      );
    } else {
      ship.controlSurfaces.set(0, 0, 0);
    }

    // ---- Main propulsion and field drive ---------------------------------
    // The throttle governor is always on, for the pilot as well as for the
    // ascent autopilot: a hand-flown climb should not require watching the q
    // and temperature gauges and backing off on them manually. It is a
    // ceiling, so a macro that has already governed its own throttle is not
    // governed twice.
    const demanded = clamp01(input.throttle);
    const governor = throttleGovernor(ship, this.governorMargin);
    ship.controls.throttle = Math.min(demanded, governor.limit);
    if (governor.reason && demanded > governor.limit + 1e-6) {
      this.failures.push({ system: 'Throttle governor', reason: `${governor.reason} limit` });
    }
    ship.controls.rotation.copy(input.rotation);
    ship.controls.translation.copy(input.translation);

    this.applyVerticalAssist(ship, env, engineEnv, dt);

    void engineEnv;
  }

  /**
   * Builds a full attitude quaternion from a desired nose direction, choosing a
   * roll reference that keeps the vessel's dorsal side sensibly oriented rather
   * than tumbling about the nose axis.
   */
  private buildAttitude(nose: Vec3, env: FlightEnvironment, nav: NavigationBasis): Quat {
    const forward = nose.normalized();
    // Prefer "up" away from the planet; fall back to the orbit normal when the
    // nose is pointed straight up or down.
    let reference = env.up;
    if (Math.abs(forward.dot(reference)) > 0.985) reference = nav.normal;
    let right = Vec3.crossVectors(reference, forward);
    if (right.lengthSq() < 1e-9) right = Vec3.anyPerpendicular(forward);
    right.normalize();
    const up = Vec3.crossVectors(forward, right).normalize();
    return Quat.fromBasis(right, up, forward);
  }

  /** Velocity, altitude and target-matching holds, expressed as RCS force. */
  private applyVelocityHolds(
    ship: Spacecraft,
    env: FlightEnvironment,
    nav: NavigationBasis,
    forceBody: Vec3,
    maxRcsForce: Vec3,
  ): void {
    const desiredDeltaV = new Vec3();
    let active = false;

    if (this.holds.matchTargetVelocity && nav.targetRelativeVelocity) {
      desiredDeltaV.add(nav.targetRelativeVelocity);
      active = true;
    } else if (this.holds.velocityHold) {
      desiredDeltaV.add(Vec3.subVectors(this.holds.velocityTarget, ship.body.velocity));
      active = true;
    }

    if (!active) return;

    // Convert the desired velocity change into a force, limited so the computer
    // does not demand more than the thrusters can produce.
    const mass = ship.massProperties.total;
    const commandWorld = desiredDeltaV.clone().mulScalar(mass * 1.5);
    const commandBody = ship.body.orientation.rotateInverse(commandWorld);

    const limited = new Vec3(
      clamp(commandBody.x, -maxRcsForce.x, maxRcsForce.x),
      clamp(commandBody.y, -maxRcsForce.y, maxRcsForce.y),
      clamp(commandBody.z, -maxRcsForce.z, maxRcsForce.z),
    );
    forceBody.add(limited);

    if (commandBody.length() > limited.length() * 1.05) {
      this.failures.push({ system: 'Velocity hold', reason: 'Requires more thrust than RCS can produce' });
    }
    void env;
  }

  /**
   * Hover, altitude and vertical-speed assistance.
   *
   * These modes are where the field drive earns its place: on a high-gravity
   * body the landing thrusters alone cannot hold the vessel up, so the computer
   * splits the demand between chemical thrust and field-drive acceleration,
   * preferring the field drive because it burns electricity rather than
   * irreplaceable propellant.
   */
  private applyVerticalAssist(
    ship: Spacecraft,
    env: FlightEnvironment,
    engineEnv: EngineEnvironment,
    dt: number,
  ): void {
    const holds = this.holds;
    const wantsVertical = holds.hoverAssist || holds.altitudeHold || holds.verticalSpeedHold;

    if (!wantsVertical) return;

    const g = env.gravity.length();
    const verticalSpeed = ship.body.velocity.dot(env.up);

    // Desired vertical acceleration.
    let desiredAccel: number;
    if (holds.altitudeHold) {
      const error = holds.altitudeTarget - env.altitude;
      // Target a climb rate proportional to the error, capped, then close on it.
      const targetRate = clamp(error * 0.25, -30, 30);
      desiredAccel = g + clamp((targetRate - verticalSpeed) * 0.9, -g * 1.5, g * 1.5);
    } else if (holds.verticalSpeedHold) {
      desiredAccel = g + clamp((holds.verticalSpeedTarget - verticalSpeed) * 0.9, -g * 1.5, g * 1.5);
    } else {
      desiredAccel = g + clamp(-verticalSpeed * 1.2, -g * 1.5, g * 1.5);
    }
    desiredAccel = Math.max(0, desiredAccel);

    const mass = ship.massProperties.total;
    let requiredForce = desiredAccel * mass;

    // Field drive takes as much of the load as it can.
    if (ship.fieldDrive) {
      const available = ship.fieldDrive.availableAcceleration(g);
      const fieldShare = Math.min(desiredAccel, available);
      if (fieldShare > 0) {
        ship.controls.fieldDriveMode = 'ascent';
        ship.controls.fieldDriveAscentAcceleration = Math.max(0, fieldShare - g);
        if (fieldShare < desiredAccel * 0.999 && available <= fieldShare + 1e-9) {
          this.failures.push({
            system: 'Field drive',
            reason: ship.fieldDrive.thermalDerate() < 1 ? 'Thermally limited' : 'At maximum output',
          });
        }
        requiredForce = Math.max(0, (desiredAccel - fieldShare) * mass);
      } else {
        ship.controls.fieldDriveMode = 'off';
      }
    }

    // Whatever remains goes to the landing thrusters, projected onto the local
    // vertical: if the vessel is tilted, its downward-firing thrusters only
    // contribute their vertical component, exactly as they should.
    const thrusters = ship.landingThrusters;
    if (thrusters.length === 0 || requiredForce <= 0) {
      if (requiredForce <= 0) ship.controls.landingThrustersArmed = thrusters.length > 0;
      return;
    }

    ship.controls.landingThrustersArmed = true;
    let totalVerticalCapacity = 0;
    const dirScratch = new Vec3();
    for (const e of thrusters) {
      const dirWorld = ship.body.orientation.rotate(e.thrustDirection(dirScratch), new Vec3());
      const vertical = Math.max(0, dirWorld.dot(env.up));
      totalVerticalCapacity += e.maxThrustAt(engineEnv) * vertical;
    }

    if (totalVerticalCapacity <= 1e-6) {
      this.failures.push({ system: 'Hover assist', reason: 'No thruster is pointing downward' });
      return;
    }

    const throttle = clamp01(requiredForce / totalVerticalCapacity);
    for (const e of thrusters) e.commandedThrottle = throttle;

    if (requiredForce > totalVerticalCapacity * 1.01) {
      this.failures.push({
        system: holds.hoverAssist ? 'Hover assist' : 'Altitude hold',
        reason: 'Insufficient thrust to support the vessel',
      });
    }
    void dt;
  }

  /** Sets the alignment mode and clears any conflicting hold. */
  setAlignment(alignment: AlignmentTarget): void {
    this.holds.alignment = alignment;
    if (alignment !== 'none') this.holds.attitudeHold = false;
    this.heldAttitude = null;
  }

  /** Convenience used by macros and the UI. */
  setFieldDriveMode(ship: Spacecraft, mode: FieldDriveMode): void {
    ship.controls.fieldDriveMode = mode;
  }
}
